import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Moon, Search } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import PageSeoNote from "@/components/PageSeoNote";
import { findKeywordConfig } from "@/constants/keywordLandings";

// Rotating tints for highlighted dream boxes (blue / red / green + purple + amber)
const TINTS = [
  { bg: "bg-gradient-to-br from-sky-500 to-blue-600", text: "text-white", chip: "bg-white/25" },
  { bg: "bg-gradient-to-br from-rose-500 to-red-600", text: "text-white", chip: "bg-white/25" },
  { bg: "bg-gradient-to-br from-emerald-500 to-green-600", text: "text-white", chip: "bg-white/25" },
  { bg: "bg-gradient-to-br from-violet-500 to-purple-600", text: "text-white", chip: "bg-white/25" },
  { bg: "bg-gradient-to-br from-amber-400 to-orange-500", text: "text-white", chip: "bg-white/30" },
];

export default function DreamNumber() {
  const { pathname } = useLocation();
  const kw = findKeywordConfig(pathname);
  useSeo({
    title: kw?.title || "Morning Teer Dream Number | Shillong Morning Teer",
    description:
      kw?.description ||
      "Look up Morning Teer Dream Numbers — traditional keyword-to-number mappings for Shillong Morning Teer players.",
    path: pathname,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Dream Number", path: pathname },
    ],
  });
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .get("/dream", { params: q ? { q } : {} })
        .then((r) => setItems(r.data.items))
        .catch(() => setItems([]));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <>
      <PageHeader title="Dream Number" subtitle="Look up your dream and see the Teer number" />
      <div className="max-w-xl mx-auto w-full px-4 pb-6 space-y-4">
        <Card className="p-4 border-slate-200 flex items-center gap-3">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            data-testid="dream-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a dream (fish, snake, water, wedding…)"
            className="border-0 focus-visible:ring-0 shadow-none px-0"
          />
        </Card>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.length === 0 ? (
            <Card className="col-span-2 sm:col-span-3 p-8 text-center border-dashed text-slate-500 text-sm">
              No matches.
            </Card>
          ) : (
            items.map((it, i) => {
              const tint = TINTS[i % TINTS.length];
              return (
                <div
                  key={it.id}
                  data-testid={`dream-card-${it.keyword}`}
                  className={`rounded-2xl ${tint.bg} ${tint.text} shadow-lg shadow-slate-900/10 p-4 flex flex-col items-center text-center hover:-translate-y-1 hover:shadow-xl transition-all`}
                >
                  <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${tint.chip} mb-2`}>
                    <Moon className="h-4 w-4" />
                  </span>
                  <div className="text-sm font-heading font-bold capitalize opacity-95">
                    {it.keyword}
                  </div>
                  <div className="font-numbers font-black text-3xl mt-1 tracking-tight">
                    {it.number}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <PageSeoNote topic="dream-number" />
    </>
  );
}
